// src/Pages/ApiKeys/ApiKeySuccessModal.tsx

import type React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon } from "@heroicons/react/solid";
import { ClipboardCopyIcon } from "lucide-react";
import { toast } from "sonner";

interface ApiKeySuccessModalProps {
  open: boolean;
  apiKey: string;
  keyName: string;
  onClose: () => void;
}

const ApiKeySuccessModal: React.FC<ApiKeySuccessModalProps> = ({
  open,
  apiKey,
  keyName,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      toast.success("API key αντιγράφηκε στο πρόχειρο");
      
      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Δεν ήταν δυνατή η αντιγραφή του API key");
    }
  };

  const handleClose = () => {
    // Clear clipboard for security (optional)
    // navigator.clipboard.writeText("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <CheckCircleIcon className="h-6 w-6 text-green-500" />
            <DialogTitle>API Key Δημιουργήθηκε Επιτυχώς</DialogTitle>
          </div>
          <DialogDescription className="mt-2">
            Το API key "{keyName}" δημιουργήθηκε επιτυχώς.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Προσοχή
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Αποθηκεύστε αυτό το API key με ασφάλεια. 
                    <strong> Δεν θα εμφανιστεί ξανά!</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Το API key σας:
            </label>
            <div className="relative">
              <div className="font-mono text-sm bg-gray-100 p-4 pr-12 rounded-lg break-all select-all">
                {apiKey}
              </div>
              <button
                onClick={handleCopyToClipboard}
                className="absolute top-2 right-2 p-2 bg-white rounded-md hover:bg-gray-50 transition-colors"
                title="Αντιγραφή στο πρόχειρο"
              >
                <ClipboardCopyIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            {copied && (
              <p className="text-sm text-green-600">
                ✓ Αντιγράφηκε στο πρόχειρο
              </p>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-2">Πώς να χρησιμοποιήσετε το API key:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Προσθέστε το API key στις ρυθμίσεις της εφαρμογής σας</li>
                <li>Χρησιμοποιήστε το header: <code className="bg-blue-100 px-1 py-0.5 rounded">X-API-Key: {apiKey.substring(0, 10)}...</code></li>
                <li>Μην μοιράζεστε ή δημοσιεύετε το API key</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button
            onClick={handleCopyToClipboard}
            variant="outline"
            disabled={copied}
          >
            {copied ? "Αντιγράφηκε!" : "Αντιγραφή API Key"}
          </Button>
          <Button onClick={handleClose}>
            Κατάλαβα
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeySuccessModal;